import { Fragment, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { ChevronDown, ChevronRight, Info } from 'lucide-react';
import MarcoReporte, {
  TD,
  TD_NUM,
  TH,
  TarjetaTotal,
} from '../../components/reportes/MarcoReporte';
import {
  CLASE_INPUT,
  Campo,
  SelectProveedor,
} from '../../components/reportes/FiltrosComunes';
import { useComprasReporte } from '../../hooks/useReportes';
import { formatearPrecio } from '../../utils/facturaCalculos';
import { formatearFechaCon, obtenerFechaActualLocal } from '../../utils/dateUtils';
import BloqueLectura from '../../components/reportes/BloqueLectura';
import {
  EtiquetasDestacado,
  claseFila,
  useDestacados,
} from '../../components/reportes/destacados';

/**
 * Reporte 4 — Compras del período (PROMPT_N°054).
 *
 * Dos vistas del mismo dato: resumen por proveedor y detalle por factura
 * con líneas expandibles.
 *
 * Las recepciones de OC sin factura van en su propia sección y **no suman
 * al total**. En QA, la carga inicial del inventario es una OC de 43
 * líneas por $2.595.800: meterla en el total diría que en octubre se
 * compraron $2,6 millones que nadie compró.
 */

const primerDiaDelMes = () => `${obtenerFechaActualLocal().slice(0, 7)}-01`;
const fecha = (f: string) =>
  formatearFechaCon(f, { day: '2-digit', month: '2-digit', year: 'numeric' });

export default function ReporteCompras() {
  const [params, setParams] = useSearchParams();
  const [vista, setVista] = useState<'resumen' | 'detalle'>('resumen');
  const [abiertas, setAbiertas] = useState<Set<number>>(new Set());

  const poner = (clave: string, valor: string) => {
    const p = new URLSearchParams(params);
    if (valor === '') p.delete(clave);
    else p.set(clave, valor);
    setParams(p, { replace: true });
  };

  const desde = params.get('desde') ?? primerDiaDelMes();
  const hasta = params.get('hasta') ?? obtenerFechaActualLocal();
  const estado = (params.get('estado') ?? 'registradas') as
    | 'registradas'
    | 'anuladas'
    | 'todas';

  /**
   * Aplica el filtro de una frase clickeable y baja a la tabla. Los filtros
   * viven en la query string (N°053), así que basta con escribirlos ahí:
   * el reporte se vuelve a consultar solo y el link queda compartible.
   */
  const aplicarFiltroDeFrase = (
    filtro: Record<string, string | number | boolean>
  ) => {
    const p = new URLSearchParams(params);
    for (const [k, v] of Object.entries(filtro)) p.set(k, String(v));
    setParams(p, { replace: true });
    document
      .getElementById('tabla-reporte')
      ?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const filtros = useMemo(
    () => ({
      desde,
      hasta,
      estado,
      proveedor_id: params.get('proveedor_id') ?? undefined,
    }),
    [desde, hasta, estado, params]
  );

  const { data, isLoading, error } = useComprasReporte(filtros);
  const destacados = useDestacados(data?.lectura);

  const alternar = (id: number) => {
    const s = new Set(abiertas);
    if (s.has(id)) s.delete(id);
    else s.add(id);
    setAbiertas(s);
  };

  const vacio =
    !isLoading &&
    (data?.facturas.length ?? 0) === 0 &&
    (data?.recepcionesSinFactura.length ?? 0) === 0;

  return (
    <MarcoReporte
      nombre="compras"
      titulo="Compras del período"
      descripcion="Facturas por proveedor y su detalle, con las recepciones sin factura aparte."
      filtros={filtros}
      cargando={isLoading}
      error={error}
      vacio={vacio}
      mensajeVacio="No hubo compras en el período."
      lectura={
        <BloqueLectura
          lectura={data?.lectura}
          ayuda="compras"
          cargando={isLoading}
          onFiltro={aplicarFiltroDeFrase}
        />
      }
      controles={
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <Campo etiqueta="Desde">
            <input
              type="date"
              className={CLASE_INPUT}
              value={desde}
              onChange={(e) => poner('desde', e.target.value)}
            />
          </Campo>
          <Campo etiqueta="Hasta">
            <input
              type="date"
              className={CLASE_INPUT}
              value={hasta}
              onChange={(e) => poner('hasta', e.target.value)}
            />
          </Campo>
          <SelectProveedor
            valor={params.get('proveedor_id') ?? ''}
            onChange={(v) => poner('proveedor_id', v)}
          />
          <Campo etiqueta="Estado">
            <select
              className={CLASE_INPUT}
              value={estado}
              onChange={(e) => poner('estado', e.target.value)}
            >
              <option value="registradas">Registradas</option>
              <option value="anuladas">Anuladas</option>
              <option value="todas">Todas</option>
            </select>
          </Campo>
        </div>
      }
      totales={
        data ? (
          <>
            <div className="grid grid-cols-1 sm:grid-cols-4 gap-4">
              <TarjetaTotal
                etiqueta="Facturas"
                valor={String(data.total.facturas)}
              />
              <TarjetaTotal
                etiqueta="Neto"
                valor={`$${formatearPrecio(data.total.neto)}`}
              />
              <TarjetaTotal
                etiqueta="IVA"
                valor={`$${formatearPrecio(data.total.iva)}`}
              />
              <TarjetaTotal
                etiqueta="Total"
                valor={`$${formatearPrecio(data.total.total)}`}
                tono="bueno"
              />
            </div>
            <div className="flex gap-2">
              {(['resumen', 'detalle'] as const).map((v) => (
                <button
                  key={v}
                  onClick={() => setVista(v)}
                  className={`px-4 py-1.5 rounded-lg text-sm font-medium border ${
                    vista === v
                      ? 'bg-blue-600 text-white border-blue-600'
                      : 'border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300'
                  }`}
                >
                  {v === 'resumen' ? 'Por proveedor' : 'Por factura'}
                </button>
              ))}
            </div>
          </>
        ) : null
      }
    >
      {vista === 'resumen' ? (
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/40">
            <tr>
              <th className={TH}>Proveedor</th>
              <th className={`${TH} text-right`}>Facturas</th>
              <th className={`${TH} text-right`}>Neto</th>
              <th className={`${TH} text-right`}>IVA</th>
              <th className={`${TH} text-right`}>Total</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
            {data?.porProveedor.map((p) => (
              <tr
                key={p.proveedor}
                className="hover:bg-gray-50 dark:hover:bg-gray-700/40"
              >
                <td className={`${TD} font-medium`}>{p.proveedor}</td>
                <td className={TD_NUM}>{p.facturas}</td>
                <td className={TD_NUM}>${formatearPrecio(p.neto)}</td>
                <td className={TD_NUM}>${formatearPrecio(p.iva)}</td>
                <td className={`${TD_NUM} font-medium`}>
                  ${formatearPrecio(p.total)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : (
        <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
          <thead className="bg-gray-50 dark:bg-gray-900/40">
            <tr>
              <th className={TH} />
              <th className={TH}>Factura</th>
              <th className={TH}>Fecha</th>
              <th className={TH}>Proveedor</th>
              <th className={`${TH} text-right`}>Neto</th>
              <th className={`${TH} text-right`}>IVA</th>
              <th className={`${TH} text-right`}>Total</th>
              <th className={`${TH} text-right`}>Líneas</th>
              <th className={TH}>Estado</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
            {data?.facturas.map((f) => {
              const abierta = abiertas.has(f.id);
              const lineas = data.lineas.filter((l) => l.factura_id === f.id);
              return (
                <Fragment key={f.id}>
                  <tr
                    onClick={() => alternar(f.id)}
                    className={`cursor-pointer ${
                      claseFila(destacados.get(f.id)) ||
                      'hover:bg-gray-50 dark:hover:bg-gray-700/40'
                    }`}
                  >
                    <td className={TD}>
                      {abierta ? (
                        <ChevronDown className="h-4 w-4 text-gray-400" />
                      ) : (
                        <ChevronRight className="h-4 w-4 text-gray-400" />
                      )}
                    </td>
                    <td className={`${TD} font-medium`}>
                      {f.numero}
                      <EtiquetasDestacado tipos={destacados.get(f.id)} />
                    </td>
                    <td className={TD}>{fecha(f.fecha)}</td>
                    <td className={TD}>{f.proveedor}</td>
                    <td className={TD_NUM}>${formatearPrecio(f.neto)}</td>
                    <td className={TD_NUM}>${formatearPrecio(f.iva)}</td>
                    <td className={`${TD_NUM} font-medium`}>
                      ${formatearPrecio(f.total)}
                    </td>
                    <td className={TD_NUM}>{f.lineas}</td>
                    <td className={TD}>
                      <span
                        className={`inline-block px-2 py-0.5 rounded-full text-xs font-medium ${
                          f.estado === 'registrada'
                            ? 'bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-300'
                            : 'bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-300'
                        }`}
                      >
                        {f.estado}
                      </span>
                    </td>
                  </tr>
                  {abierta && (
                    <tr>
                      <td colSpan={9} className="bg-gray-50 dark:bg-gray-900/30 px-6 py-3">
                        <table className="min-w-full">
                          <thead>
                            <tr>
                              <th className={TH}>Ingrediente</th>
                              <th className={`${TH} text-right`}>Cantidad</th>
                              <th className={`${TH} text-right`}>Precio unit.</th>
                              <th className={`${TH} text-right`}>Subtotal</th>
                              <th className={TH}>Lote creado</th>
                            </tr>
                          </thead>
                          <tbody>
                            {lineas.map((l, i) => (
                              <tr key={i}>
                                <td className={TD}>{l.ingrediente}</td>
                                <td className={TD_NUM}>
                                  {l.cantidad.toLocaleString('es-CL')} {l.unidad}
                                </td>
                                <td className={TD_NUM}>
                                  ${formatearPrecio(l.precio_unitario)}
                                </td>
                                <td className={TD_NUM}>
                                  ${formatearPrecio(l.subtotal)}
                                </td>
                                <td className={`${TD} font-mono text-xs`}>
                                  {l.lote ?? '—'}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </td>
                    </tr>
                  )}
                </Fragment>
              );
            })}
          </tbody>
        </table>
      )}

      {data && data.recepcionesSinFactura.length > 0 && (
        <div className="border-t-2 border-gray-200 dark:border-gray-700 mt-2">
          <div className="px-4 py-3 bg-amber-50 dark:bg-amber-900/20 flex items-start gap-2">
            <Info className="h-4 w-4 text-amber-600 dark:text-amber-400 mt-0.5 flex-shrink-0" />
            <div>
              <h3 className="text-sm font-semibold text-amber-900 dark:text-amber-200">
                Recepciones sin factura
              </h3>
              <p className="text-xs text-amber-800 dark:text-amber-300">
                Entraron a bodega pero no hay factura asociada.{' '}
                <strong>No están sumadas en el total de compras.</strong>
              </p>
            </div>
          </div>
          <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700">
            <thead className="bg-gray-50 dark:bg-gray-900/40">
              <tr>
                <th className={TH}>Orden de compra</th>
                <th className={TH}>Recepción</th>
                <th className={TH}>Proveedor</th>
                <th className={TH}>Estado</th>
                <th className={`${TH} text-right`}>Lotes</th>
                <th className={`${TH} text-right`}>Total</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700/60">
              {data.recepcionesSinFactura.map((r) => (
                <tr key={r.id}>
                  <td className={`${TD} font-medium`}>{r.numero_orden}</td>
                  <td className={TD}>{r.fecha ? fecha(r.fecha) : '—'}</td>
                  <td className={TD}>{r.proveedor ?? '—'}</td>
                  <td className={TD}>{r.estado}</td>
                  <td className={TD_NUM}>{r.lineas}</td>
                  <td className={`${TD_NUM} font-medium`}>
                    ${formatearPrecio(r.total)}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </MarcoReporte>
  );
}
